import classNames from 'classnames'
import { JSX, RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { useOnInputKeyPress } from 'core-components/library/dropdown'
import { usePopupOpenedState } from 'core-components/library/dropdown/hooks/usePopupOpenedState'
import { Input } from 'core-components/library/inputs'
import { MultiSelectInput } from 'core-components/library/multiSelect/baseMultiSelect/MultiSelectInput'
import { ItemsLine } from 'core-components/library/multiSelect/ItemsLine'
import { ICommonMultiselect } from 'core-components/library/multiSelect/models/ICommonMultiselect'
import { IItemsLine } from 'core-components/library/multiSelect/models/IItemsLine'
import { IPopupMethods } from 'core-components/library/popup'
import { Select } from 'core-components/library/selects'
import { ValidateInput } from 'core-components/library/validation'

import styles from './BaseMultiSelect.module.scss'

interface IBaseMultiSelect<T> {
  wip: boolean
  onChangeInputValueHandler?: (text: string) => void
  inputted: string
  setInputted: (newValue: string) => void
  items: T[]
  currentValue: any[]
  setCurrentValue: (newValue: IForm[]) => void
  inputRef: RefObject<HTMLInputElement | null>
  itemTemplate: IItemsLine['itemTemplate']

  beforeRemoveHandler?: IItemsLine['beforeRemoveHandler']

  popupHeaderWithoutSelection?: () => JSX.Element
  popupHeaderAfterLines?: () => JSX.Element
}

export const BaseMultiSelect = <T, R>({
  value,
  source,
  label,
  placeholder,
  caption,
  onChange,
  disabled,
  useRowCheckbox,
  onCheck,
  checkedValues,
  checkFn,
  valueDependencies,
  margin,
  classes,
  limit,
  renderFunction,
  useDrag,
  prepareInputConfig,
  hierarchyField,
  expanded,
  _popupVisible,
  InputComponent = Input,
  ValidateInputComponent = ValidateInput,
  closeOnSelect = true,
  beforeRemoveHandler,
  itemTemplate,
  itemClickCallback,
  wip,
  onChangeInputValueHandler,
  inputted,
  setInputted,
  items,
  popupHeaderWithoutSelection,
  popupHeaderAfterLines,
  currentValue,
  setCurrentValue,
  inputRef,
}: ICommonMultiselect<T, R> & IBaseMultiSelect<T>) => {
  const popupRef = useRef<IPopupMethods | null>(null)
  const [showSelectedItems, setShowSelectedItems] = useState<boolean>(false)
  const { setPopupActions, onKeyPress } = useOnInputKeyPress()

  const hasHierarchy = typeof hierarchyField !== 'undefined' ? !!hierarchyField : undefined
  const selectClasses = {
    ...classes?.controlClasses,
    tableRoot: classNames(
      {
        ['content-hidden']: showSelectedItems,
      },
      classes?.controlClasses?.tableRoot
    ),
  }
  const { visibilityCallbackHandler, isPopupOpened } = usePopupOpenedState()

  const handleClick = (item: T) => {
    inputRef.current?.focus()
    const updated = [...value, item[source.keyField]].slice(0, limit)
    setInputted('')
    onChange(updated, items)
  }

  const onDeleteValue = (delValue: string, moveFocusToInput: boolean) => {
    if (moveFocusToInput) {
      inputRef.current?.focus()
    }
    const updated = value.filter((el) => '' + el !== '' + delValue)
    setInputted('')
    onChange(updated, items)
  }

  const onCheckHandle = (item: T, value: boolean) => {
    inputRef.current?.focus()
    if (onCheck) {
      onCheck(item, value, items, inputted)
    }
  }

  const initializePopupRef = useCallback((selectPopupRef: RefObject<IPopupMethods | null>) => {
    if (selectPopupRef.current) {
      setPopupActions(selectPopupRef)
      popupRef.current = selectPopupRef.current
    }
  }, [])

  useEffect(() => {
    if (inputted) {
      popupRef.current?.open()
    }
  }, [inputted])

  return (
    <Select<T, R>
      wip={wip}
      classes={selectClasses}
      margin={margin}
      isSelect
      visibilityCallback={visibilityCallbackHandler}
      initializePopupRef={initializePopupRef}
      renderFunction={renderFunction}
      itemClickCallback={itemClickCallback}
      useRowCheckbox={useRowCheckbox}
      source={source}
      caption={caption}
      onClick={handleClick}
      disabled={disabled}
      checkedValues={checkedValues}
      onCheck={onCheckHandle}
      closeOnSelect={closeOnSelect}
      popupHeader={() => {
        if (!showSelectedItems) {
          if (popupHeaderWithoutSelection) {
            popupHeaderWithoutSelection()
          }
        }
        return (
          <>
            {limit ? <p className={styles.limit}>Limit - {limit} value</p> : null}
            {showSelectedItems ? (
              <ItemsLine
                beforeRemoveHandler={beforeRemoveHandler}
                disabled={disabled}
                useDrag={useDrag}
                caption={caption}
                itemTemplate={itemTemplate}
                onChange={(value, items) => onChange(value, items as T[])}
                className={styles.popupLabels}
                value={currentValue}
                onDeleteValue={(value) => onDeleteValue(value[source.keyField], false)}
                onDataLoad={(renderedItems) => {
                  if (!renderedItems.length) {
                    setShowSelectedItems(false)
                  }
                }}
              />
            ) : null}
            {popupHeaderAfterLines ? popupHeaderAfterLines() : null}
          </>
        )
      }}
      _popupVisible={_popupVisible}
      expanded={expanded}
      hasHierarchy={hasHierarchy}
      hierarchyField={hierarchyField}
    >
      <MultiSelectInput<T, R>
        classes={classes?.inputClasses}
        beforeRemoveHandler={beforeRemoveHandler}
        onChange={onChange}
        disabled={disabled}
        caption={caption}
        itemTemplate={itemTemplate}
        label={label}
        placeholder={placeholder}
        useDrag={useDrag}
        source={source}
        onKeyPress={onKeyPress}
        isPopupOpened={isPopupOpened}
        prepareInputConfig={prepareInputConfig}
        checkFn={checkFn || null}
        valueDependencies={valueDependencies}
        ValidateInputComponent={ValidateInputComponent}
        InputComponent={InputComponent}
        currentValue={currentValue}
        setCurrentValue={setCurrentValue}
        items={items}
        setShowSelectedItems={setShowSelectedItems}
        onChangeInputValueHandler={onChangeInputValueHandler}
        onDeleteValue={onDeleteValue}
        inputted={inputted}
        setInputted={setInputted}
        inputRef={inputRef}
        validatorValue={!wip ? currentValue.join('') : 'Loading data...'}
      />
    </Select>
  )
}
